/**
 * 滚动入场动画 —— 对齐 Shopify Dawn 官方主题的实现
 *
 * 参考源码(可直接核对):
 *   Dawn assets/animations.js                  ← 本文件的逻辑来源
 *   Dawn assets/base.css  第 3299-3360 行       ← CSS 参数来源
 *   初始态:  opacity .01 + 下移 2rem
 *   播放:    animation: --animation-slide-in   (600ms cubic-bezier(0,0,0.3,1))
 *   错峰:    animation-delay: calc(--animation-order * 75ms)   (Dawn 用 75ms)
 *   变量:    --duration-extra-long: 600ms;  --ease-out-slow: cubic-bezier(0, 0, 0.3, 1);
 *
 * ⚠️ 维护提醒一:本文件顶部的块注释里不要再出现块注释符号。JS 注释不嵌套,
 *    里面出现一次结束符号就会把外层注释提前结束,剩余部分被当作代码解析,
 *    整个文件语法错误 → 脚本一行都不执行 → 全站动画消失。
 *    (CSS 里同样的问题叫"坏注释尾巴",会吞掉紧随其后的一整条规则。)
 *    改完务必跑:node --check <文件>
 *
 * ⚠️ 维护提醒二:错峰序号封顶在 MAX_STAGGER_ORDER。不封顶的话,一次进场元素很多时
 *    (例如页脚 18 个链接同批进入)最后一个要等 18*75ms ≈ 1.35s 才出现,反而拖沓。
 *
 * 机制:
 *   1. JS 给目标元素加 .scroll-trigger.animate--slide-in + data-cascade,并 observe;
 *   2. 进入视口时摘掉 .scroll-trigger--offscreen → 动画播放;
 *   3. data-cascade 的元素写入 --animation-order(同批进场的序号),按 75ms 递进错峰。
 *
 * 四个关键取舍(改之前先读):
 *   · 首屏内容不做动画 —— 脚本在 DOMContentLoaded 后才跑,若把已经在视口里的元素藏起来,
 *     用户会看到"先出现再消失再淡入"。所以初次扫描只处理首屏下方的元素。
 *   · 但运行时新插入的内容例外(懒加载区块 / 筛选换掉的网格):它们还没被绘制过,
 *     直接参与动画不会闪,反而比"突然出现"自然。You may also like 就靠这条生效。
 *   · 已被动画元素的内部不再重复动画(卡片里的文字块、博客卡里的封面图)。
 *   · 用 translate 属性而不是 transform:主题卡片悬停效果用的是 transform,
 *     动画 fill 状态会把它盖住,分开写两者互不干扰。
 *
 * 2026-10-11 性能优化(行为不变,详见各处注释):
 *   a) 首屏扫描改为"先读完所有 rect,再统一写类",消除读-写交替造成的强制重排;
 *   b) MutationObserver 回调只登记,扫描合并到下一帧(rAF)执行,不再在页面正忙时
 *      同步跑十几个选择器;
 *   c) 错峰序号只统计"本次真正进场"的条目,不再用包含未相交条目的批次下标(会跳号)。
 */
(() => {
  const TRIGGER_CLASS = 'scroll-trigger';
  const ANIMATION_CLASS = 'animate--slide-in';
  const OFFSCREEN_CLASS = 'scroll-trigger--offscreen';
  const CANCEL_CLASS = 'scroll-trigger--cancel';

  /** 错峰序号上限:最多 5*75ms = 375ms 的递进,再往后同批元素一起出现 */
  const MAX_STAGGER_ORDER = 5;

  // 对齐 Dawn 的覆盖面:它逐块挂类,连标题、说明文字、整个滑块都在动。
  // 这里用"内容单元"来近似:文字块、图片块、菜单、各种卡片。
  const TARGET_SELECTOR_LIST = [
    // 注意这里排除了 .rte 容器本身:它通常是"一整篇文章正文"或"整段商品描述",
    // 作为单个动画单元太大(往往比一屏还高,首屏防闪规则会让它整块都不动),
    // 而且会把内部本该单独进场的段落/图片全部按"嵌套"跳过。
    // 正文改成下面的 .rte > * 逐个单元进场。
    '.text-block:not(.rte)', // 标题 / 正文 / 说明文字(卡片内部的会被嵌套规则跳过)
    '.image-block', // 主题的图片块(区块里的图片 / 横幅)
    '.menu__heading', // 页脚/侧栏菜单的列标题(头部区域整体被排除,不会波及超级菜单)
    '.menu__item', // 菜单链接项
    // 页脚版权(.footer-utilities__text)**刻意不作为动画目标**:
    // 它是页面上最后一个元素,下面只有 10px,永远满足不了 IntersectionObserver 的
    // "越过视口底部往上 80px"判定 → 会被永久留在 opacity .01(2026-10-09 踩过这个坑,
    // 表现为"版权文字消失")。法律声明这类文字本来也不该做入场动画。
    '.product-card', // 商品卡:首页区块 / 集合页 / 搜索 / You may also like
    '.collection-card', // 集合卡
    '.featured-blog-posts-card', // 首页博客块
    // 博客列表页:原来把整张卡片 .blog-post-item 列为目标(标题、摘要跟着一起淡入)。
    // 2026-10-09 起博客只让图片动,所以整卡不再是目标,只留下面这张封面图。
    '.blog-post-card__image-container', // 博客卡封面图(列表页里会被嵌套规则过滤)
    // 系列轮播区块(sections/custom-pop-carousel.liquid)。
    // **只接容器,不接里面的卡片**:那个区块的卡片用 JS 逐帧写 --pc-t,再由 CSS 算出
    // `opacity: calc(1 - 0.4 * var(--pc-t))`(离中心越远越淡)。本系统的播放规则
    // `.scroll-trigger:not(...).animate--slide-in { animation: … forwards }` 里 forwards
    // 会把 opacity 钉死在 1(动画优先于普通声明) → 会把那个"越远越淡"整个抹掉。
    // 接在容器上则完全正交:容器淡入上浮一次,区块内部的缩放/淡出/Tab 切换动画各管各的。
    '.pcarousel',
  ];

  // 博客页(文章页 / 博客列表页):**只让图片做入场动画**,文字一律不动。
  // 读文章时标题、段落、小标题逐段淡入反而干扰阅读;图片淡入则不影响。
  //
  // 判定为什么用 URL:主题的 body 上只在文章页输出 template-article
  // (layout/theme.liquid 里 `{% if template.name == 'article' %} template-article{% endif %}`),
  // **列表页没有 template-blog**,所以列表页只能靠路径判定;/blogs/... 一次覆盖两种页面。
  const isBlogPage =
    document.body.classList.contains('template-article') || window.location.pathname.startsWith('/blogs/');
  if (isBlogPage) {
    // 1) 去掉所有"文字类"目标(页脚菜单也在内 —— 博客页上文字统一不参与)
    for (const textSelector of ['.text-block:not(.rte)', '.menu__heading', '.menu__item']) {
      const index = TARGET_SELECTOR_LIST.indexOf(textSelector);
      if (index >= 0) TARGET_SELECTOR_LIST.splice(index, 1);
    }
    // 2) 正文里只认图片:用 .rte img,而不是 .rte > *(后者会把段落、小标题、列表一起动)。
    //    用后代选择器而不是 .rte > img,是因为正文插图有的包在 <p> 里。
    TARGET_SELECTOR_LIST.push('.rte img');
  }

  const TARGET_SELECTOR = TARGET_SELECTOR_LIST.join(', ');

  // 这些容器里不做入场动画:头部区域(含公告栏)、抽屉、弹窗、预测搜索。
  // 它们是常驻/开合式 UI,滚动动画在里面只会显得奇怪。
  const EXCLUDE_SELECTOR = [
    '.announcement-bar',
    'header',
    '.header-section',
    'dialog',
    'cart-drawer',
    '.cart-drawer',
    '.predictive-search',
    'predictive-search',
    '.mega-menu',
    '.menu-drawer',
  ].join(', ');

  function onIntersection(entries, observer) {
    // 错峰序号只统计"本次真正进场"的条目:
    // entries 里还包含未相交的条目,直接用它们的下标会跳号(0、2、3、5),
    // 文档里靠后的元素反而可能先动。(2026-10-11)
    let order = 0;

    entries.forEach((entry) => {
      const element = entry.target;

      if (entry.isIntersecting) {
        if (element.classList.contains(OFFSCREEN_CLASS)) {
          element.classList.remove(OFFSCREEN_CLASS);
          // 同一批进场的元素依次递增,形成瀑布式错峰(Dawn 原版逻辑,这里加了上限)
          if (element.hasAttribute('data-cascade')) {
            element.style.setProperty('--animation-order', Math.min(order, MAX_STAGGER_ORDER));
            order += 1;
          }
        }
        observer.unobserve(element);
      } else {
        // 注意:元素只要进过一次视口,上面分支里的 observer.unobserve() 就把它永久移出观察,
        // 这个 else 分支不会再为它执行 —— 所以实际行为是"只播一次",不会滚回来重播。
        // (Dawn 同样是 unobserve,这一点与 Dawn 一致,别再按"重播"去理解或改它。)
        element.classList.add(OFFSCREEN_CLASS);
        element.classList.remove(CANCEL_CLASS);
      }
    });
  }

  const observer = new IntersectionObserver(onIntersection, {
    // 比 Dawn 原版(-50px)多留一点:元素刚探进视口底部就触发,节奏更自然
    rootMargin: '0px 0px -80px 0px',
  });

  /** 已被动画元素的内部不重复动画 */
  function isNested(element) {
    return element.parentElement?.closest(TARGET_SELECTOR) != null;
  }

  /** 常驻/开合式 UI 内部不参与 */
  function isExcluded(element) {
    return element.closest(EXCLUDE_SELECTOR) != null;
  }

  function collect(roots) {
    const found = [];
    for (const root of roots) {
      if (!(root instanceof Element)) continue;
      if (root.matches(TARGET_SELECTOR) && !root.classList.contains(TRIGGER_CLASS)) found.push(root);
      for (const element of root.querySelectorAll(TARGET_SELECTOR)) {
        if (!element.classList.contains(TRIGGER_CLASS)) found.push(element);
      }
    }
    return found.filter((element) => !isNested(element) && !isExcluded(element));
  }

  /**
   * @param {Element[]} roots 扫描范围
   * @param {boolean} onlyBelowFold true = 只处理首屏下方的元素(首次载入,避免闪一下);
   *                               false = 全部参与(运行时新插入的内容还没绘制过,不会闪)
   */
  function initialize(roots = [document.documentElement], onlyBelowFold = true) {
    let candidates = collect(roots);

    // 读取阶段:一次性量完所有 rect。
    // 原来是"量一个 → 写一个类 → 再量下一个":classList.add 会让布局失效,
    // 于是下一次 getBoundingClientRect() 触发同步重排 —— 首屏几十个元素就是几十次重排。
    // 把读和写彻底分开,重排降到一次。(2026-10-11)
    if (onlyBelowFold) {
      const fold = window.innerHeight;
      candidates = candidates.filter((element) => element.getBoundingClientRect().top >= fold);
    }

    // 写入阶段:统一挂类 + 开始观察(这一趟不再读布局)
    for (const element of candidates) {
      element.classList.add(TRIGGER_CLASS, ANIMATION_CLASS);
      element.setAttribute('data-cascade', '');
      observer.observe(element);
    }
  }

  window.addEventListener('DOMContentLoaded', () => initialize());

  // 懒加载区块(You may also like)、集合页筛选/排序换掉的网格,都要重新接管。
  //
  // DOM 插入在页面上非常频繁(购物车抽屉更新、预测搜索每敲一个字换一批结果…),
  // 所以回调里只"登记",真正的扫描合并到下一帧执行 —— 避免在页面正忙时同步跑
  // 十几个选择器和一次 filter。一帧内多次插入自动合并成一次扫描。(2026-10-11)
  let pendingRoots = [];
  let frameRequested = false;

  function scheduleInitialize(roots) {
    for (const root of roots) pendingRoots.push(root);
    if (frameRequested) return;
    frameRequested = true;
    requestAnimationFrame(() => {
      frameRequested = false;
      const batch = pendingRoots;
      pendingRoots = [];
      if (batch.length) initialize(batch, false);
    });
  }

  new MutationObserver((mutations) => {
    const added = [];
    for (const mutation of mutations) {
      for (const node of mutation.addedNodes) {
        if (node instanceof Element) added.push(node);
      }
    }
    if (added.length) scheduleInitialize(added);
  }).observe(document.documentElement, { childList: true, subtree: true });

  if (window.Shopify && window.Shopify.designMode) {
    document.addEventListener('shopify:section:load', (event) => initialize([event.target], false));
  }
})();
